<template>
  <div class="gg-settings">
    <GloboGraphicsHeader />

    <div class="gg-settings__body">
      <section class="gg-settings__card">
        <div class="gg-settings__intro">
          <router-link :to="{ name: 'globographics' }" class="gg-settings__back">
            <v-icon class="gg-settings__back-icon">mdi-arrow-left</v-icon>
            <span class="gg-settings__back-label">Voltar</span>
          </router-link>
          <h1 class="gg-settings__title">Configurações avançadas</h1>
        </div>

        <div class="gg-settings__content">
          <div class="gg-segmented" role="tablist">
            <button
              v-for="tab in tabs"
              :key="tab.key"
              type="button"
              role="tab"
              :id="`gg-settings-tab-${tab.key}`"
              :aria-selected="String(activeTab === tab.key)"
              :aria-controls="`gg-settings-panel-${tab.key}`"
              class="gg-segmented__item"
              :class="{ 'gg-segmented__item--active': activeTab === tab.key }"
              @click="activeTab = tab.key"
            >
              <v-icon class="gg-segmented__icon">{{ tab.icon }}</v-icon>
              <span class="gg-segmented__label">{{ tab.label }}</span>
            </button>
          </div>

          <div
            :id="`gg-settings-panel-${activeTab}`"
            role="tabpanel"
            :aria-labelledby="`gg-settings-tab-${activeTab}`"
          >
            <GloboGraphicsSitesManager v-if="activeTab === 'sites'" active />
            <PlayoutEnginesManager v-else active :site-sigla="selectedSiteSigla" />
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import GloboGraphicsHeader from "@/components/GloboGraphics/GloboGraphicsHeader.vue";
import GloboGraphicsSitesManager from "@/components/GloboGraphics/GloboGraphicsSitesManager.vue";
import PlayoutEnginesManager from "@/components/GloboGraphics/PlayoutEnginesManager.vue";

export default {
  name: "GloboGraphicsSettingsView",
  components: { GloboGraphicsHeader, GloboGraphicsSitesManager, PlayoutEnginesManager },
  data() {
    return {
      activeTab: "sites",
      tabs: [
        { key: "sites", label: "Sites", icon: "mdi-map-marker" },
        { key: "engines", label: "Engines", icon: "mdi-tune-vertical" },
      ],
    };
  },
  computed: {
    ...mapGetters("sites", { selectedSiteSigla: "getterSelectedSiteSigla" }),
  },
};
</script>

<style lang="scss" scoped>
// TODO(v2-primitive): .gg-settings and GloboGraphicsView's .gg-shell are the two
// module shells. Promote to a shared primitive if a third GG route appears.
.gg-settings {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background: var(--sc-gg-surface-3);
  font-family: "Inter", "Globotipo", sans-serif;
}

.gg-settings__body {
  flex: 1 1 auto;
  // The frame centres a 1417px card in 1920 (≈251px gutters) and drops it 40px
  // below the 56px header.
  padding: 40px var(--sc-space-6) var(--sc-space-6);
}

.gg-settings__card {
  display: flex;
  flex-direction: column;
  gap: 32px;
  width: 100%;
  max-width: 1417px;
  margin: 0 auto;
  padding: 32px 32px 40px;
  background: var(--sc-gg-surface-2);
  border-radius: var(--sc-radius-xl);
}

.gg-settings__intro {
  display: flex;
  flex-direction: column;
  gap: var(--sc-space-4);
  align-items: flex-start;
}

.gg-settings__back {
  display: inline-flex;
  align-items: center;
  // Fixed, not min-height: the design clamps this control to 40 and the vertical
  // padding would otherwise push it to 44.
  height: 40px;
  padding: 0 6px;
  border-radius: var(--sc-radius-md);
  text-decoration: none;
  transition: background var(--sc-duration-fast) var(--sc-easing-standard);

  &:hover {
    background: rgba(var(--sc-gg-accent-rgb), 0.08);
  }

  &:focus-visible {
    outline: 2px solid var(--sc-gg-accent);
    outline-offset: 2px;
  }
}

.gg-settings__back-icon {
  color: var(--sc-gg-accent) !important;
  font-size: 20px !important;
}

.gg-settings__back-label {
  // The global `span { color }` rule would otherwise grey this label.
  padding: 0 var(--sc-space-1);
  color: var(--sc-gg-accent);
  font-size: 14px;
  font-weight: var(--sc-weight-medium);
  line-height: 16px;
  letter-spacing: 0.07px;
}

.gg-settings__title {
  display: flex;
  align-items: center;
  // The design's title sits in a 32px row with the 28px line centred in it.
  height: 32px;
  margin: 0;
  color: var(--sc-gg-text-title);
  font-size: 24px;
  font-weight: var(--sc-weight-semi);
  line-height: 28px;
}

.gg-settings__content {
  display: flex;
  flex-direction: column;
  gap: 32px;
}

/* Segmented Sites / Engines switcher */
.gg-segmented {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--sc-space-1);
  height: 56px;
  padding: var(--sc-space-2);
  background: var(--sc-gg-surface-3);
  border-radius: var(--sc-radius-xl);
}

.gg-segmented__item {
  display: flex;
  flex: 1 0 0;
  align-items: center;
  justify-content: center;
  gap: var(--sc-space-2);
  min-width: 0;
  height: 100%;
  padding: var(--sc-space-2) var(--sc-space-3);
  background: transparent;
  border: 2px solid transparent;
  border-radius: var(--sc-radius-md);
  cursor: pointer;
  // Inactive segments are dimmed rather than recoloured, so the active one reads
  // as raised out of the strip.
  opacity: 0.4;
  transition: opacity var(--sc-duration-fast) var(--sc-easing-standard),
              background var(--sc-duration-fast) var(--sc-easing-standard);

  &:hover {
    opacity: 0.7;
  }

  &:focus-visible {
    outline: 2px solid var(--sc-gg-accent);
    outline-offset: -2px;
  }
}

.gg-segmented__item--active {
  background: var(--sc-gg-surface-1);
  border-color: rgba(77, 78, 81, 0.3);
  opacity: 1;

  &:hover {
    opacity: 1;
  }
}

.gg-segmented__icon {
  color: var(--sc-gg-text-title) !important;
  font-size: 24px !important;
}

.gg-segmented__label {
  color: var(--sc-gg-text-title);
  font-size: 14px;
  font-weight: var(--sc-weight-medium);
  line-height: 16px;
  letter-spacing: 0.07px;
  white-space: nowrap;
}
</style>
